import { useState } from 'react';
import { Shield, X } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { useDealershipSettings, DEFAULT_SETTINGS } from '@/data/hooks/useDealershipSettings';
import { useAuth } from '@/contexts/AuthContext';

const DISMISS_KEY = 'pullover.house_rules.dismissed.v1';

/**
 * Read-only "House Rules" card shown to reps. Uses real settings in real mode,
 * defaults in demo. Self-hides when no rules are enabled, or when dismissed
 * for the session.
 */
export function HouseRulesCard() {
  const { mode } = useAuth();
  const { settings, loading } = useDealershipSettings();
  const [dismissed, setDismissed] = useState(() => {
    try { return sessionStorage.getItem(DISMISS_KEY) === '1'; } catch { return false; }
  });

  if (loading || dismissed) return null;

  const s = mode === 'real' ? settings : DEFAULT_SETTINGS;
  const rules: string[] = [];
  if (s.require_next_step) rules.push('Every deal needs a next step.');
  if (s.followup_sla_hours > 0) rules.push(`Follow up within ${s.followup_sla_hours}h of first contact.`);
  if (s.min_daily_activities > 0) rules.push(`Log at least ${s.min_daily_activities} activities per day.`);
  if (s.stale_deal_hours > 0) rules.push(`Deals quiet for ${s.stale_deal_hours}h get flagged stale.`);
  if (s.hot_requires_review) rules.push('Hot deals get a manager glance — keep notes tight.');

  if (rules.length === 0) return null;

  const handleDismiss = () => {
    try { sessionStorage.setItem(DISMISS_KEY, '1'); } catch { /* noop */ }
    setDismissed(true);
  };

  return (
    <Card className="border-primary/20 bg-primary/5">
      <CardContent className="p-4">
        <div className="flex items-start gap-3">
          <Shield className="h-4 w-4 text-primary shrink-0 mt-0.5" />
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium mb-1.5">House Rules</p>
            <ul className="text-xs text-muted-foreground space-y-1 list-disc list-inside">
              {rules.map(r => <li key={r}>{r}</li>)}
            </ul>
          </div>
          <button onClick={handleDismiss} aria-label="Dismiss house rules" className="text-muted-foreground hover:text-foreground shrink-0">
            <X className="h-4 w-4" />
          </button>
        </div>
      </CardContent>
    </Card>
  );
}